import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { allArticles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';

const SITE_URL = 'https://vigorbuy.courses';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg';

const articleImages: Record<string, string> = {
  'article-guide-01': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20scene%20with%20a%20sleek%20laptop%20displaying%20organized%20spreadsheet%20data%20on%20screen%2C%20a%20steaming%20ceramic%20coffee%20mug%2C%20a%20notebook%20with%20handwritten%20notes%2C%20warm%20morning%20sunlight%20streaming%20through%20a%20window%2C%20soft%20natural%20shadows%2C%20cream%20and%20sand%20colored%20desk%20setup%2C%20editorial%20product%20photography%2C%20high%20detail%2C%20calm%20productive%20atmosphere&width=800&height=600&seq=art-guide-v2&orientation=landscape',
  'article-hoodies-01': 'https://readdy.ai/api/search-image?query=Elegant%20flatlay%20photography%20of%20three%20cozy%20hoodies%20in%20sage%20green%2C%20oatmeal%20cream%2C%20and%20charcoal%20gray%20arranged%20on%20a%20light%20stone%20surface%2C%20soft%20diffused%20natural%20lighting%2C%20editorial%20fashion%20catalog%20style%2C%20fabric%20texture%20visible%2C%20minimalist%20composition%2C%20warm%20neutral%20tones%2C%20professional%20product%20display&width=800&height=600&seq=art-hoodies-v1&orientation=landscape',
  'article-compare-01': 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20concept%20with%20two%20contrasting%20shopping%20experiences%2C%20left%20side%20showing%20a%20clean%20organized%20spreadsheet%20interface%20on%20a%20laptop%2C%20right%20side%20showing%20chaotic%20multiple%20browser%20tabs%2C%20soft%20warm%20lighting%2C%20editorial%20style%2C%20cream%20and%20sandstone%20color%20palette%2C%20professional%20photography%2C%20symbolic%20visual%20storytelling&width=800&height=600&seq=art-compare-v1&orientation=landscape',
  'article-categories-01': 'https://readdy.ai/api/search-image?query=Artistic%20overhead%20flatlay%20of%20fashion%20and%20lifestyle%20products%20neatly%20arranged%20in%20labeled%20sections%20on%20a%20light%20oak%20wooden%20surface%2C%20sneakers%2C%20watches%2C%20bags%2C%20sunglasses%2C%20and%20accessories%20organized%20by%20category%2C%20soft%20natural%20window%20light%2C%20warm%20earthy%20tones%2C%20editorial%20catalog%20photography%2C%20clean%20minimalist%20aesthetic&width=800&height=600&seq=art-categories-v1&orientation=landscape',
  'article-compare-02': 'https://readdy.ai/api/search-image?query=Close%20up%20photography%20of%20a%20person%20using%20a%20tablet%20with%20a%20product%20comparison%20interface%20displaying%20multiple%20fashion%20items%20side%20by%20side%2C%20warm%20ambient%20lighting%2C%20soft%20cream%20background%2C%20modern%20lifestyle%20scene%2C%20editorial%20style%2C%20shallow%20depth%20of%20field%2C%20professional%20composition&width=800&height=600&seq=art-compare2-v1&orientation=landscape',
  'article-bags-01': 'https://readdy.ai/api/search-image?query=Stylish%20product%20photography%20of%20three%20premium%20bags%20including%20a%20leather%20backpack%2C%20a%20canvas%20messenger%20bag%2C%20and%20a%20minimalist%20tote%2C%20arranged%20on%20a%20warm%20terracotta%20toned%20surface%2C%20soft%20diffused%20natural%20lighting%2C%20editorial%20fashion%20catalog%20style%2C%20rich%20texture%20details%2C%20elegant%20composition&width=800&height=600&seq=art-bags-v1&orientation=landscape',
  'article-tips-01': 'https://readdy.ai/api/search-image?query=Overhead%20shot%20of%20a%20creative%20workspace%20with%20sticky%20notes%2C%20bullet%20journal%2C%20colorful%20markers%2C%20and%20a%20laptop%20showing%20a%20clean%20organized%20interface%2C%20warm%20natural%20light%20from%20a%20window%2C%20soft%20cream%20and%20sand%20background%2C%20cozy%20productive%20atmosphere%2C%20editorial%20style%2C%20inspirational%20learning%20concept&width=800&height=600&seq=art-tips-v1&orientation=landscape',
  'article-accessories-01': 'https://readdy.ai/api/search-image?query=Luxury%20product%20flatlay%20of%20elegant%20accessories%20including%20a%20minimalist%20watch%20with%20leather%20strap%2C%20classic%20aviator%20sunglasses%2C%20a%20thin%20gold%20chain%20necklace%2C%20and%20a%20leather%20cardholder%2C%20arranged%20on%20a%20soft%20sage%20green%20linen%20surface%2C%20warm%20diffused%20lighting%2C%20editorial%20jewelry%20photography%2C%20refined%20aesthetic&width=800&height=600&seq=art-accessories-v1&orientation=landscape',
  'article-fashion-01': 'https://readdy.ai/api/search-image?query=Artistic%20fashion%20moodboard%20concept%20with%20fabric%20swatches%2C%20outfit%20sketches%2C%20and%20product%20photos%20of%20coordinated%20clothing%20pieces%20arranged%20on%20a%20warm%20beige%20textured%20background%2C%20soft%20natural%20light%2C%20creative%20editorial%20style%2C%20fashion%20design%20inspiration%2C%20elegant%20composition&width=800&height=600&seq=art-fashion-v1&orientation=landscape',
  'article-clothing-01': 'https://readdy.ai/api/search-image?query=Clean%20product%20photography%20of%20a%20curated%20clothing%20rack%20with%20a%20denim%20jacket%2C%20white%20cotton%20tee%2C%20beige%20chinos%2C%20and%20canvas%20sneakers%20styled%20together%2C%20warm%20natural%20lighting%2C%20soft%20cream%20background%2C%20editorial%20fashion%20catalog%2C%20lifestyle%20product%20display%2C%20minimalist%20aesthetic&width=800&height=600&seq=art-clothing-v1&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);

  const articleJsonLd = useMemo(() => {
    if (!article) return null;

    const articleUrl = `${SITE_URL}/article/${article.slug}`;
    const articleImage = articleImages[article.imageSeq] || articleImages['article-guide-01'];

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BlogPosting',
          '@id': `${articleUrl}/#article`,
          'headline': article.title,
          'description': article.excerpt,
          'datePublished': article.date,
          'dateModified': article.date,
          'inLanguage': 'en-US',
          'image': articleImage,
          'author': {
            '@type': 'Organization',
            '@id': `${SITE_URL}/#organization`,
            'name': 'VigorBuy.courses',
          },
          'publisher': {
            '@type': 'Organization',
            '@id': `${SITE_URL}/#organization`,
            'name': 'VigorBuy.courses',
            'logo': { '@type': 'ImageObject', 'url': LOGO_URL },
          },
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': articleUrl,
          },
          'about': article.category,
          'wordCount': article.sections.reduce(
            (acc, s) => acc + s.paragraphs.reduce((pAcc, p) => pAcc + p.split(' ').length, 0),
            0,
          ),
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${articleUrl}/#breadcrumb`,
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': SITE_URL },
            { '@type': 'ListItem', 'position': 2, 'name': 'Shopping Guide', 'item': `${SITE_URL}/shopping-guide` },
            { '@type': 'ListItem', 'position': 3, 'name': article.title },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${articleUrl}/#faq`,
          'mainEntity': article.faqs.map((faq) => ({
            '@type': 'Question',
            'name': faq.q,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': faq.a,
            },
          })),
        },
      ],
    };
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
            <h1 className="text-3xl font-heading font-bold text-foreground-950">Article Not Found</h1>
            <p className="mt-4 text-foreground-500">The article you are looking for does not exist.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap">
              <i className="ri-home-4-line"></i> Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      {articleJsonLd && <JsonLd data={articleJsonLd} />}
      <Navbar />
      <main className="pt-20">
        <article className="w-full">
          <header className="w-full bg-background-100/60 border-b border-background-200/50">
            <div className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
                <span className="text-foreground-300">/</span>
                <Link to="/shopping-guide" className="hover:text-foreground-700 transition-colors">Shopping Guide</Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-600 font-medium truncate max-w-[200px]">{article.title}</span>
              </nav>

              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">{article.category}</span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <p className="mt-4 text-base md:text-lg text-foreground-500 leading-relaxed max-w-2xl">
                {article.excerpt}
              </p>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
            <div className="space-y-10">

              {article.sections.map((section, sIdx) => (
                <section key={sIdx}>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className={`text-base text-foreground-600 leading-relaxed ${pIdx > 0 ? 'mt-4' : ''}`}>
                      {para}
                    </p>
                  ))}
                </section>
              ))}

              {article.faqs.length > 0 && (
                <section>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {article.faqs.map((faq, idx) => (
                      <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                          <span className="text-base font-semibold text-foreground-900 pr-4">{faq.q}</span>
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-45 transition-transform duration-200">
                            <i className="ri-add-line text-lg"></i>
                          </div>
                        </summary>
                        <div className="px-5 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              <section className="bg-primary-50 rounded-2xl border border-primary-200/50 p-8 md:p-10 text-center">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  Ready to Start Discovering?
                </h2>
                <p className="mt-3 text-base text-foreground-600 max-w-lg mx-auto">
                  Now that you have learned more about the Vigorbuy Spreadsheet, put that knowledge into action. Open the spreadsheet and start exploring products today.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                  >
                    Open vigorbuy sheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-7 py-3 bg-foreground-950 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap"
                  >
                    Back to Homepage
                    <i className="ri-home-4-line"></i>
                  </Link>
                </div>
              </section>

            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}